import { instana } from "@crystal/analytics";
import * as rtk from "@reduxjs/toolkit";
import { addAuthnApiReducers } from "../ap2/state.js";
import { getReduxHost } from "../store/state/redux-host.js";
import "../store/state/host.js";
import "../store/state/modal.js";
import "../store/storage/memory-storage.js";
import "mitt";
import "../store/storage/universal-memory-storage.js";
import { passwordResetStartApiAction, passwordResetCompleteAction, registrationApiAction, loginApiAction, lightRegistrationApiAction, logoutApiAction, pageContextAction, authContextAction, runtimeInfoAction, portalOpenAction, portalCloseAction, createPasswordApiAction } from "./actions.js";
import { addExternalAuthReducers } from "./external-auth-reducers.js";
import { addProfileReducers } from "./profile-reducers.js";
import { addSessionReducers } from "./session-reducers.js";
import { accountPortalStateName } from "./types.js";
import { updateLegacyUserWithSession } from "./utils.js";
const { createSlice, isRejectedWithValue } = { ...rtk }.default || rtk;
const accountPortalSlice = createSlice({
  name: accountPortalStateName,
  initialState: getInitialState,
  reducers: {},
  extraReducers(builder) {
    builder = addExternalAuthReducers(builder);
    builder = addAuthnApiReducers(builder);
    builder = addSessionReducers(builder);
    builder = addProfileReducers(builder);
    builder.addCase(passwordResetStartApiAction.pending, (state) => {
      state.eventType = "passwordResetStart";
      state.status = "pending";
      state.authContext = {
        hasUser: false
      };
      delete state.error;
    }).addCase(passwordResetStartApiAction.fulfilled, (state) => {
      state.eventType = "passwordResetStart";
      state.status = "success";
    }).addCase(passwordResetCompleteAction.pending, (state) => {
      state.eventType = "passwordResetComplete";
      state.status = "pending";
      state.authContext = {
        hasUser: true
      };
    }).addCase(passwordResetCompleteAction.fulfilled, (state, { payload }) => {
      var _a;
      if ((_a = state.authContext) == null ? void 0 : _a.isAuthHandled) {
        return;
      }
      state.eventType = "passwordResetComplete";
      state.status = "success";
      state.user = updateLegacyUserWithSession(payload);
      state.isAuthenticated = true;
      logSessionInfo(payload);
    }).addCase(registrationApiAction.pending, (state, { payload }) => {
      state.eventType = "registration";
      state.status = "pending";
      state.registrationData = payload;
      state.authContext = {
        hasUser: true
      };
      delete state.error;
    }).addCase(registrationApiAction.fulfilled, (state, { payload }) => {
      var _a;
      if ((_a = state.authContext) == null ? void 0 : _a.isAuthHandled) {
        return;
      }
      const user = updateLegacyUserWithSession(payload);
      state.eventType = "registration";
      state.status = "success";
      state.user = user;
      state.isAuthenticated = true;
      state.isPersistent = user.session.isPersistent;
      delete state.registrationData;
      logSessionInfo(payload);
    }).addCase(loginApiAction.pending, (state, { payload }) => {
      state.eventType = "login";
      state.status = "pending";
      state.loginCredentials = payload;
      state.authContext = {
        hasUser: true
      };
      delete state.error;
    }).addCase(loginApiAction.fulfilled, (state, { meta, payload }) => {
      var _a;
      if ((_a = state.authContext) == null ? void 0 : _a.isAuthHandled) {
        return;
      }
      const user = updateLegacyUserWithSession(payload, { isReauthenticated: meta.arg.reauthenticate });
      state.eventType = "login";
      state.status = "success";
      state.user = user;
      state.isAuthenticated = true;
      state.isPersistent = user.session.isPersistent;
      state.isReauthenticated = meta.arg.reauthenticate;
      delete state.loginCredentials;
      if (!meta.arg.reauthenticate) {
        logSessionInfo(payload);
      }
    }).addCase(lightRegistrationApiAction.pending, (state, { payload }) => {
      state.eventType = "lightRegistration";
      state.status = "pending";
      delete state.lightUser;
      state.lightRegistrationData = payload;
      delete state.error;
      state.authContext = {
        hasUser: false
      };
    }).addCase(lightRegistrationApiAction.fulfilled, (state, { meta, payload }) => {
      state.eventType = "lightRegistration";
      state.status = "success";
      state.lightUser = {
        ...payload,
        email: meta.arg.email
      };
      delete state.lightRegistrationData;
      instana("user", payload.acid);
    }).addCase(logoutApiAction.pending, (state) => {
      state.eventType = "logout";
      state.status = "pending";
      state.authContext = {
        hasUser: false
      };
      delete state.error;
    }).addCase(logoutApiAction.fulfilled, (state) => {
      state.eventType = "logout";
      state.status = "success";
      state.isAuthenticated = false;
      delete state.user;
      delete state.isPersistent;
      delete state.authContext;
      delete state.kmc;
      delete state.lightUser;
      delete state.profileData;
      delete state.error;
    }).addCase(pageContextAction, (state, { payload }) => {
      state.pageContext = payload;
    }).addCase(authContextAction, (state, { payload }) => {
      state.authContext = payload;
    }).addCase(runtimeInfoAction, (state, { payload }) => {
      for (const [key, val] of Object.entries(payload)) {
        state[key] = val;
      }
    }).addCase(portalOpenAction.fulfilled, (state, { payload }) => {
      state.isModalVisible = payload;
    }).addCase(portalCloseAction.fulfilled, (state) => {
      state.isModalVisible = false;
      delete state.status;
      delete state.eventType;
      delete state.error;
    }).addCase(createPasswordApiAction.pending, (state) => {
      state.eventType = "createPassword";
      state.status = "pending";
      state.authContext = {
        hasUser: true
      };
      delete state.error;
    }).addCase(createPasswordApiAction.fulfilled, (state, { payload }) => {
      var _a;
      if ((_a = state.authContext) == null ? void 0 : _a.isAuthHandled) {
        return;
      }
      const user = updateLegacyUserWithSession(payload);
      state.eventType = "createPassword";
      state.status = "success";
      state.user = user;
      state.isAuthenticated = true;
      state.isPersistent = user.session.isPersistent;
      logSessionInfo(payload);
    }).addMatcher(isRejectedWithValue, (state, { payload }) => {
      state.status = "error";
      state.error = payload;
    });
  }
});
let lastState;
function initializeAccountPortalState({ force = false } = {}) {
  const redux = getReduxHost();
  if (!redux.isReducerRegistered(accountPortalSlice.name)) {
    return redux.registerReducer(accountPortalSlice.name, accountPortalSlice.reducer);
  }
  if (force) {
    lastState = redux.getStore().getState().accountPortal;
    redux.registerReducer(accountPortalSlice.name, accountPortalSlice.reducer, true);
  }
}
function getInitialState() {
  return {
    isModalVisible: false,
    ...lastState
  };
}
function logSessionInfo(user) {
  instana("user", user.acid);
  instana("meta", "isLoggedin", true);
  instana("meta", "isPersistent", user.isPersistent);
}
export {
  accountPortalSlice,
  initializeAccountPortalState
};
//# sourceMappingURL=reducers.js.map
